import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Container, Box, Typography, Tab, Tabs, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'

// import Tooltip from 'components/Common/ToolTip'
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from 'components/Common/ToolTip/InfoTooltip'

import MainLayout from 'layouts/mainLayout'

import Navbar from 'components/Common/Navbar'
import TabPanel from 'components/Common/TabPanel'
import { a11yProps } from 'components/Common/TabPanel'

import Info from 'components/MyBusiness/Info'
import Profile from 'components/MyBusiness/Profile'
import KeyPeople from 'components/MyBusiness/KeyPeople'
import BusinessAttributes from 'components/MyBusiness/BusinessAttributes'

import { tealPieChartColor, greyPieChartColor } from 'themes/theme'
// import IdleTimer from 'components/Common/IdleTimer'
import GradientCircularProgress from 'components/Common/GradientCircularProgress'

import routes from 'routes'
import api from 'api'
import { store } from 'reduxStore'

const tabName = {
  profile: 0,
  info: 1,
  keyPeople: 2,
  businessAttributes: 3,
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  maxWidth: '620px',
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '1.5rem',
    color: theme.palette.primary + ' !important',
    minWidth: '30px',
    fontFamily: 'poppins',
    fontWeight: 'lighter',
  },
  '& .Mui-selected': {
    fontWeight: 'bold',
    color: theme.palette.primary,
  },
}))

const StyledTab = styled(Tab)(({ theme, isSelected }) => ({
  color: theme.palette.text.primary,
}))

const MyBusiness = () => {
  const dashboardInfo = useSelector(state => state.apiResponseReducers.dashboardInfo)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = store.getState().userReducer.user
  const [searchParams] = useSearchParams()
  const [value, setValue] = useState(0)
  const [selectedBusiness, setSelectedBusiness] = useState(null)
  const [isChartLoading, setIsChartLoading] = useState(true)

  searchParams.get('tab')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [navigate])

  useEffect(() => {
    if (searchParams.get('tab')) {
      const selectedTab = tabName[searchParams.get('tab')] || 0
      setValue(selectedTab)
    }
  }, [searchParams])

  const handleChange = (event, newValue) => {
    const selectedTab = Object.keys(tabName).find(t => tabName[t] === newValue)
    navigate({
      pathname: '/claimed-business',
      search: `?tab=${selectedTab}`,
    })
  }

  useEffect(() => {
    if (!user) return

    fetchChartData()
  }, [user])

  const fetchChartData = async () => {
    await api.getDashboardInfo()
    setIsChartLoading(false)
  }

  const businessProfileCompletion = dashboardInfo?.business_info?.business_profile_completion
    ? parseInt(dashboardInfo?.business_info?.business_profile_completion)
    : 0

  const businessChartData = [
    {
      share: dashboardInfo?.business_info?.business_profile_completion
        ? parseInt(dashboardInfo?.business_info?.business_profile_completion)
        : 0,
      color: tealPieChartColor,
    },
    {
      share:
        100 -
        (dashboardInfo?.business_info?.business_profile_completion
          ? parseInt(dashboardInfo?.business_info?.business_profile_completion)
          : 0),
      color: greyPieChartColor,
    },
  ]

  async function getUserBusiness() {
    const data = await api.getUsersBusinesses()
    const [obj] = data
    setSelectedBusiness(obj)
  }

  useEffect(() => {
    if (!user) return

    if (user.kybValidated !== undefined) {
      getUserBusiness()
    }
  }, [user])

  const updateBusiness = useCallback(
    business => {
      if (business) {
        setSelectedBusiness(business)
        dispatch({ type: 'claimedBusinessUpdated', payload: business })
      }
    },
    [dispatch],
  )
  if (!user) {
    return <Navigate to={routes.auth.signIn} />
  }

  return (
    <MainLayout>
      {/* <IdleTimer maxIdleTime={process.env['REACT_APP_MAX_IDLE_TIME_DASHBOARD']} /> */}
      <Navbar isDashboard={false} />
      <div className="container-fluid">
        <div className="row">
          <main>
            <Container maxWidth="lg">
              <Box
                mt={4}
                sx={{
                  display: 'grid',
                  gridAutoColumns: '1fr',
                  gap: 1,
                }}
              >
                <Grid item sx={{ gridRow: '1', gridColumn: 'span 3', marginBottom: '-10px' }}>
                  <Grid container flexDirection="column">
                    <Grid item>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '20px',
                            display: 'inline-block',
                            color: 'var(--primary)',
                            fontFamily: 'poppins',
                            letterSpacing: '-0.3px',
                            marginBottom: '20px',
                          }}
                        >
                          Claimed Business&nbsp;
                          <Tooltip
                            content={
                              <Typography variant="body2" color="white">
                                The “Claimed Business” section includes editable and non-editable fields. If you see any
                                incorrect or outdated information on your business page that you cannot edit, please
                                fill out a ticket by clicking the words “contact us” and we will work with you to update
                                it.
                              </Typography>
                            }
                          />
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <StyledTabs
                      value={value}
                      onChange={handleChange}
                      aria-label="Claimed business profile"
                      allowScrollButtonsMobile={true}
                      TabIndicatorProps={{
                        style: { display: 'none' },
                      }}
                    >
                      <StyledTab disableFocusRipple={true} label="Profile" {...a11yProps(0)} />
                      <StyledTab disableFocusRipple={true} label="Info" {...a11yProps(1)} />
                      <StyledTab disableFocusRipple={true} label="Key People" {...a11yProps(2)} />
                      <StyledTab disableFocusRipple={true} label="Business Attributes" {...a11yProps(3)} />
                    </StyledTabs>
                  </Grid>
                </Grid>

                {user?.kybValidated !== undefined && (
                  <Grid item sx={{ gridRow: '1', gridColumn: '4 / 5' }}>
                    <Grid container flexDirection="column" alignItems="center">
                      <Grid item>
                        {businessChartData && !isChartLoading && (
                          <GradientCircularProgress
                            size="small"
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                            value={businessProfileCompletion}
                            title={'Claimed Business'}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  marginBottom: '20px',
                }}
              >
                <Grid style={{ marginTop: '20px', marginBottom: '20px', borderBottom: '1px solid #CCCBD9' }} />
                <TabPanel value={value} index={0}>
                  <Profile model={user} business={selectedBusiness} setBusiness={updateBusiness} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                  <Info model={selectedBusiness} setBusiness={updateBusiness} />
                </TabPanel>
                <TabPanel value={value} index={2}>
                  <KeyPeople model={selectedBusiness} setBusiness={updateBusiness} />
                </TabPanel>
                <TabPanel value={value} index={3}>
                  <BusinessAttributes business={selectedBusiness} setBusiness={updateBusiness} />
                </TabPanel>
              </Box>
            </Container>
          </main>
        </div>
      </div>
    </MainLayout>
  )
}

export default MyBusiness
